import {
  flow,
  types,
} from 'mobx-state-tree';
import { CustomerNotificationEvents } from '../../helpers/customerNotificationEvents';
import { CustomerNotificationOrganizationSettingsDtoModel, DeliveryMethod, NotificationDateFormat, NotificationTimeFormat, TemplateDeliveryMethod } from './models';
import CustomerNotificationSettingsQuery from './queries/customerNotification/CustomerNotificationSettingsQuery';
import getAppRoot from './utils/getAppRoot';

export enum TemplateReviewDecision {
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export { TemplateDeliveryMethod };

export interface TemplateReviewResult {
  event: CustomerNotificationEvents;
  deliveryMethod: TemplateDeliveryMethod;
  decision: TemplateReviewDecision;
  reason: string;
}

export interface TemplateEditInput {
  event: CustomerNotificationEvents;
  deliveryMethod: TemplateDeliveryMethod;
  body: string;
  subject?: string;
}

const EVENT_TO_WIRE: Record<CustomerNotificationEvents, string> = {
  [CustomerNotificationEvents.DELIVERY_SCHEDULED]: 'DELIVERY_SCHEDULED',
  [CustomerNotificationEvents.ON_THE_WAY]: 'ON_THE_WAY',
  [CustomerNotificationEvents.SUCCESSFUL_DELIVERY]: 'SUCCESSFUL_DELIVERY',
  [CustomerNotificationEvents.MISSED_DELIVERY]: 'MISSED_DELIVERY',
};

const WIRE_TO_EVENT: Record<string, CustomerNotificationEvents> = Object.fromEntries(
  Object.entries(EVENT_TO_WIRE).map(([event, wire]) => [wire, event as CustomerNotificationEvents]),
);

const DECISION_FROM_WIRE: Record<string, TemplateReviewDecision> = {
  APPROVED: TemplateReviewDecision.APPROVED,
  REJECTED: TemplateReviewDecision.REJECTED,
};

const CustomerNotification = types.model('CustomerNotification')
  .props({
    cnSettings: types.maybeNull(CustomerNotificationOrganizationSettingsDtoModel),
    selectedTab: types.optional(types.enumeration<CustomerNotificationEvents>('CustomerNotificationEvents',
      Object.values(CustomerNotificationEvents)), CustomerNotificationEvents.DELIVERY_SCHEDULED),
  })
  .views((self) => ({
    get isCustomerNotificationEnabled(): boolean {
      return true;
    },
    get hasAnyEventEnabled(): boolean {
      return Object.values(CustomerNotificationEvents).some(
        (event) => self.cnSettings?.[event]?.enabled === true,
      );
    },
    get isNotificationsEnabled(): boolean {
      return self.cnSettings?.deliveryMethod !== DeliveryMethod.NONE
        && self.cnSettings?.deliveryMethod != null;
    },
  }))
  .actions((self) => ({
    getCNSettings: flow(function* () {
      try {
        const res = yield getAppRoot(self).model.queryGetCustomerNotificationSettings({}, CustomerNotificationSettingsQuery, { fetchPolicy: 'no-cache' }).currentPromise();
        self.cnSettings = res.getCustomerNotificationSettings;
      } catch (err) {
        self.cnSettings = null;
      }
    }),
    updateDeliveryMethod: flow(function* (deliveryMethod: DeliveryMethod) {
      const res = yield getAppRoot(self).model.mutatePatchCustomerNotificationSettings(
        { deliveryMethod }, qb => qb.deliveryMethod, () => {
          if (self.cnSettings) {
            self.cnSettings.deliveryMethod = deliveryMethod;
          }
        },
      ).currentPromise();

      if (self.cnSettings) { self.cnSettings.deliveryMethod = res.patchCustomerNotificationSettings.deliveryMethod; }
    }),
    updateToggle: flow(function* (enabled: boolean, eventName: CustomerNotificationEvents) {
      const res = yield getAppRoot(self).model.mutatePatchCustomerNotificationSettings(
        { [eventName]: enabled }, qb => qb[eventName](b => b.enabled),
      ).currentPromise();

      if (self.cnSettings?.[eventName]?.enabled != null) {
        self.cnSettings[eventName].enabled = res.patchCustomerNotificationSettings[eventName].enabled;
      }
    }),
    updateDeliveryWindow: flow(function* (
      deliveryWindow: number,
      deliveryWindowOption: 'etaWindowMinutesBefore' | 'etaWindowMinutesAfter' | 'driverOnTheWayThreshold',
    ) {
      const res = yield getAppRoot(self).model.mutatePatchCustomerNotificationSettings(
        { [deliveryWindowOption]: deliveryWindow }, qb => qb[deliveryWindowOption], () => {
          if (self.cnSettings?.[deliveryWindowOption] != null) {
            self.cnSettings[deliveryWindowOption] = deliveryWindow;
          }
        },
      ).currentPromise();
      if (self.cnSettings?.[deliveryWindowOption] != null) {
        self.cnSettings[deliveryWindowOption] = res.patchCustomerNotificationSettings?.[deliveryWindowOption];
      }
    }),
    // `null` lead time means "send when the route is published" (immediate); a number means
    // "send that many minutes before the route starts". Kept separate from updateDeliveryWindow
    // because that action's `!= null` guards cannot represent clearing the value back to null.
    updateDeliveryScheduledLeadTime: flow(function* (minutes: number | null) {
      const res = yield getAppRoot(self).model.mutatePatchCustomerNotificationSettings(
        { deliveryScheduledThreshold: minutes }, qb => qb.deliveryScheduledThreshold, () => {
          if (self.cnSettings) {
            self.cnSettings.deliveryScheduledThreshold = minutes;
          }
        },
      ).currentPromise();
      if (self.cnSettings) {
        self.cnSettings.deliveryScheduledThreshold = res.patchCustomerNotificationSettings?.deliveryScheduledThreshold ?? null;
      }
    }),
    switchDeliveryMethodAndResetEvents: flow(function* (deliveryMethod: DeliveryMethod) {
      const events = Object.values(CustomerNotificationEvents);
      const payload = {
        deliveryMethod,
        ...Object.fromEntries(events.map((event) => [event, false])),
      };
      const res = yield getAppRoot(self).model.mutatePatchCustomerNotificationSettings(
        payload,
        (qb: any) => events.reduce((q: any, event) => q[event]((b: any) => b.enabled), qb).deliveryMethod,
      ).currentPromise();

      const result = res.patchCustomerNotificationSettings;
      if (self.cnSettings && result?.deliveryMethod != null) {
        self.cnSettings.deliveryMethod = result.deliveryMethod;
      }
      events.forEach((event) => {
        if (self.cnSettings?.[event]?.enabled != null && result?.[event]?.enabled != null) {
          self.cnSettings[event].enabled = result[event].enabled;
        }
      });
    }),
    updateSelectedTab(selectedTab: CustomerNotificationEvents) {
      self.selectedTab = selectedTab;
    },
    updateEmailConfiguration: flow(function* (input: {
      fromName?: string;
      replyToEmail?: string;
      businessName?: string;
      phoneNumber?: string;
      address?: string;
    }) {
      const res = yield getAppRoot(self).model.mutatePatchCustomerNotificationSettings(
        input,
        qb => qb.fromName.replyToEmail.businessName.phoneNumber.address,
      ).currentPromise();
      const result = res.patchCustomerNotificationSettings;
      if (self.cnSettings && result) {
        if (result.fromName !== undefined) self.cnSettings.fromName = result.fromName ?? null;
        if (result.replyToEmail !== undefined) self.cnSettings.replyToEmail = result.replyToEmail ?? null;
        if (result.businessName !== undefined) self.cnSettings.businessName = result.businessName ?? null;
        if (result.phoneNumber !== undefined) self.cnSettings.phoneNumber = result.phoneNumber ?? null;
        if (result.address !== undefined) self.cnSettings.address = result.address ?? null;
      }
    }),
    updateDateFormat: flow(function* (dateFormat: NotificationDateFormat) {
      const res = yield getAppRoot(self).model.mutatePatchCustomerNotificationSettings(
        { dateFormat },
        qb => qb.dateFormat,
      ).currentPromise();
      if (self.cnSettings) {
        self.cnSettings.dateFormat = res.patchCustomerNotificationSettings.dateFormat ?? null;
      }
    }),
    updateTimeFormat: flow(function* (timeFormat: NotificationTimeFormat) {
      const res = yield getAppRoot(self).model.mutatePatchCustomerNotificationSettings(
        { timeFormat },
        qb => qb.timeFormat,
      ).currentPromise();
      if (self.cnSettings) {
        self.cnSettings.timeFormat = res.patchCustomerNotificationSettings.timeFormat ?? null;
      }
    }),
    reviewAndSaveTemplate: flow(function* (edit: TemplateEditInput) {
      const query = `mutation reviewAndSaveCustomerNotificationTemplate($edit: CustomerNotificationTemplateEditInput!) {
        reviewAndSaveCustomerNotificationTemplate(edit: $edit) {
          event
          deliveryMethod
          decision
          reason
        }
      }`;
      const variables = {
        edit: {
          event: EVENT_TO_WIRE[edit.event],
          deliveryMethod: edit.deliveryMethod,
          body: edit.body,
          ...(edit.subject !== undefined ? { subject: edit.subject } : {}),
        },
      };
      type ReviewMutationResponse = {
        reviewAndSaveCustomerNotificationTemplate: {
          event: string; deliveryMethod: string; decision: string; reason: string;
        };
      };
      const res = (yield getAppRoot(self).model
        .mutate<ReviewMutationResponse>(query, variables)
        .currentPromise()) as ReviewMutationResponse | undefined;
      const raw = res?.reviewAndSaveCustomerNotificationTemplate;

      if (!raw) {
        return undefined;
      }

      const result: TemplateReviewResult = {
        event: WIRE_TO_EVENT[raw.event] ?? (raw.event as CustomerNotificationEvents),
        deliveryMethod: (raw.deliveryMethod as TemplateDeliveryMethod) ?? edit.deliveryMethod,
        decision: DECISION_FROM_WIRE[raw.decision] ?? TemplateReviewDecision.REJECTED,
        reason: raw.reason,
      };

      if (result.decision === TemplateReviewDecision.APPROVED) {
        const eventSettings = self.cnSettings?.[edit.event];
        if (edit.deliveryMethod === TemplateDeliveryMethod.SMS && eventSettings?.smsTemplate) {
          eventSettings.smsTemplate.body = edit.body;
        } else if (eventSettings?.emailTemplate) {
          eventSettings.emailTemplate.body = edit.body;
          if (edit.subject !== undefined) {
            eventSettings.emailTemplate.subject = edit.subject;
          }
        }
      }

      return result;
    }),
    getTemplatePlaceholders: flow(function* () {
      const query = `query getCustomerNotificationTemplatePlaceholders {
        getCustomerNotificationTemplatePlaceholders {
          event
          placeholders
        }
      }`;
      type PlaceholdersQueryResponse = {
        getCustomerNotificationTemplatePlaceholders: Array<{ event: string; placeholders: string[] }>;
      };
      const res = (yield getAppRoot(self).model
        .query<PlaceholdersQueryResponse>(query, {})
        .currentPromise()) as PlaceholdersQueryResponse | undefined;
      const raw = res?.getCustomerNotificationTemplatePlaceholders ?? [];
      return raw.map((p) => ({
        event: WIRE_TO_EVENT[p.event] ?? (p.event as CustomerNotificationEvents),
        placeholders: p.placeholders,
      }));
    }),
  }));

export default CustomerNotification;
